import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRACTICE_AREA_LABELS = {
  asylum: 'Asylum',
  family: 'Family-Based',
  employment: 'Employment-Based',
  deportation: 'Deportation Defense',
  naturalization: 'Naturalization',
  other: 'Other',
};

const DISCLAIMER = 'Not a lawyer referral service. Does not endorse or recommend any attorney. Verify credentials independently.';

const ContactModal = ({ provider, onClose }) => {
  const [formData, setFormData] = useState({ from_name: '', from_email: '', message: '', company: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/providers/${provider.id}/contact`, {
        from_name: formData.from_name,
        from_email: formData.from_email,
        message: formData.message,
        company: formData.company, // honeypot — must stay empty for real users
      });
      setSuccess(true);
      setFormData({ from_name: '', from_email: '', message: '', company: '' });
    } catch (err) {
      console.error('Failed to send inquiry:', err);
      setError(err.response?.data?.detail || 'Failed to send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg border border-slate-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-navy-700">
            Contact {provider.display_name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-6">
              <div className="text-green-600 text-5xl mb-4" aria-hidden="true">✓</div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Message Sent!</h4>
              <p className="text-gray-600 mb-6">
                Your inquiry has been sent to {provider.display_name}. They will follow up with you directly.
              </p>
              <button
                onClick={onClose}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 font-medium text-sm">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="contact-from-name" className="block text-gray-700 text-sm font-bold mb-2">Your Name</label>
                <input
                  id="contact-from-name"
                  type="text"
                  name="from_name"
                  value={formData.from_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="contact-from-email" className="block text-gray-700 text-sm font-bold mb-2">Your Email</label>
                <input
                  id="contact-from-email"
                  type="email"
                  name="from_email"
                  value={formData.from_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-gray-700 text-sm font-bold mb-2">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  placeholder="Briefly describe your situation and how they can help…"
                  required
                />
              </div>

              {/* Honeypot field — visually hidden from real users, left for bots to fill in.
                  Intentionally NOT type="hidden" so automated form-fillers still populate it. */}
              <div className="sr-only" aria-hidden="true">
                <label htmlFor="company">Leave this field blank</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <p className="text-xs text-gray-500 mb-4">
                <span aria-hidden="true">⚠️</span> {DISCLAIMER} Only the information you type here is shared with this provider.
              </p>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-200 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 font-semibold"
                >
                  {submitting ? 'Sending…' : 'Send Message'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const ProviderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const fetchProvider = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${API}/providers/${id}`);
        setProvider(res.data);
      } catch (err) {
        console.error('Failed to load provider:', err);
        setError('This provider profile could not be found.');
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile…</p>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-xl text-center border border-slate-200 max-w-md">
          <svg className="w-12 h-12 text-slate-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'This provider does not exist or is no longer active.'}</p>
          <Link
            to="/find-legal-help"
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg transition-colors font-semibold inline-block"
          >
            ← Back to Search
          </Link>
        </div>
      </div>
    );
  }

  const practiceAreas = provider.practice_areas || [];
  const languages = provider.languages || [];
  const statesLicensed = provider.states_licensed || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto flex justify-between items-center px-4 py-3">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <svg className="w-7 h-7 text-navy-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.97zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.97z" /></svg>
            <h1 className="text-xl font-bold text-navy-700">Find Legal Help</h1>
          </div>
          <nav aria-label="Provider profile">
            <button
              onClick={() => navigate('/find-legal-help')}
              className="flex items-center text-slate-600 hover:text-navy-700 font-medium transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
              Back to Search
            </button>
          </nav>
        </div>
      </header>

      <main id="main-content" className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-8">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Photo */}
              <div className="flex-shrink-0">
                {provider.photo_url ? (
                  <img
                    src={provider.photo_url}
                    alt={provider.display_name}
                    className="w-32 h-32 rounded-full object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center" aria-hidden="true">
                    <svg className="w-12 h-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>
                  </div>
                )}
              </div>

              {/* Basic info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h2 className="text-2xl font-bold text-navy-700">{provider.display_name}</h2>
                  {provider.is_verified && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span aria-hidden="true">✓</span> Verified
                    </span>
                  )}
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 mb-3">
                  <svg className="w-3.5 h-3.5 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>
                  {provider.provider_type === 'firm' ? 'Law Firm' : 'Attorney'}
                </span>

                {(provider.city || provider.state) && (
                  <p className="text-gray-600 mb-1">
                    <svg className="w-4 h-4 inline mr-1 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0115 0z" /></svg>{[provider.address, provider.city, provider.state, provider.zip].filter(Boolean).join(', ')}
                  </p>
                )}

                {provider.bar_number && (
                  <p className="text-gray-600 mb-1"><svg className="w-4 h-4 inline mr-1 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" /></svg>Bar Number: {provider.bar_number}</p>
                )}

                <div className="flex flex-wrap gap-3 mt-4">
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-6 rounded-lg transition-colors font-semibold"
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg> Contact this provider
                  </button>
                  {provider.website && (
                    <a
                      href={provider.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-100 text-blue-800 hover:bg-blue-200 py-2.5 px-6 rounded-lg font-semibold transition-colors"
                    >
                      <svg className="w-4 h-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg> Website
                    </a>
                  )}
                  {provider.phone && (
                    <a
                      href={`tel:${provider.phone}`}
                      className="bg-blue-100 text-blue-800 hover:bg-blue-200 py-2.5 px-6 rounded-lg font-semibold transition-colors"
                    >
                      <svg className="w-4 h-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg> {provider.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Bio */}
            {provider.bio && (
              <div className="mt-8">
                <h3 className="font-bold text-gray-800 mb-2 text-lg">About</h3>
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">{provider.bio}</p>
              </div>
            )}

            {/* Details grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
              {practiceAreas.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">Practice Areas</h3>
                  <div className="flex flex-wrap gap-2">
                    {practiceAreas.map((pa) => (
                      <span key={pa} className="text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-full">
                        {PRACTICE_AREA_LABELS[pa] || pa}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {languages.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">Languages Spoken</h3>
                  <div className="flex flex-wrap gap-2">
                    {languages.map((lang) => (
                      <span key={lang} className="text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-full">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {statesLicensed.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">States Licensed</h3>
                  <div className="flex flex-wrap gap-2">
                    {statesLicensed.map((st) => (
                      <span key={st} className="text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300 px-3 py-1 rounded-full">
                        {st}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(provider.consultation_type || provider.consultation_mode) && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">Consultation</h3>
                  <div className="flex flex-wrap gap-2">
                    {provider.consultation_type && (
                      <span className="text-sm font-medium bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full capitalize">
                        {provider.consultation_type}
                      </span>
                    )}
                    {provider.consultation_mode && (
                      <span className="text-sm font-medium bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full capitalize">
                        {provider.consultation_mode}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compliance disclaimer */}
        <p className="text-sm text-gray-500 text-center mt-6 max-w-2xl mx-auto">
          <span aria-hidden="true">⚠️</span> {DISCLAIMER}
        </p>
      </main>

      {showContactModal && (
        <ContactModal provider={provider} onClose={() => setShowContactModal(false)} />
      )}
    </div>
  );
};

export default ProviderProfile;
